import type { PreferenceStatus } from '#app/shared/types/BrokerCarrierPreferenceTypes'
import type { EquipmentTypeResponseItem } from '#app/shared/types/EquipmentTypes'

export interface Carrier {
  carrier_id: string
  carrier_name: string
}

// Note: there is deliberately no LOAD_SEARCH member. A carrier's load search
// history is private to that carrier -- it powers their own recommendations and
// is never returned to brokers by /api/v1/trucklist/search.
export enum TrucklistPostType {
  HOME_BASE = 'home_base',
  FAVORITE_LANE = 'favorite_lane',
  POST_TRUCK = 'post_truck',
  LOCATION = 'location'
}

export interface TrucklistResultType {
  id: string
  carrier_id: string
  user_id: number
  carrier_name: string
  broker_sharing_enabled: boolean
  publish_availability: boolean
  mc_number: string
  date_granted_authority: string
  pickup_city: string
  pickup_state: string
  pickup_location: [number, number]
  pickup_matched_home: boolean
  pickup_date: string
  dropoff_city: string
  dropoff_state: string
  dropoff_location: [number, number]
  dropoff_matched_home: boolean
  home_city: string
  home_state: string
  home_location: [number, number]
  equipment_type: string[]
  max_weight: number | null
  max_deadhead: number | null
  post_type: TrucklistPostType
  post_source: string
  created_at: string
  updated_at: string
  row_number: number
  relationship: boolean
  preference: PreferenceStatus | null
  telephone: string | null
}

export interface TrucklistResponseType {
  data: TrucklistResultType[]
  total: number
}

export interface FormattedTrucklistResultType extends TrucklistResultType {
  formatted_pickup_date: string
  formatted_equipment_type: EquipmentTypeResponseItem[]
}
export interface Address {
  city: string
  state: string
  lat: number
  lng: number
  timeZoneId: string
  timeZoneName?: string
  timeZoneOffset?: number
  timeZoneDstOffset?: number
}
export interface LoadOfferFormDetails {
  previousOffer?: string
  loadPrice: string
  loadID: string
  /** The standardized equipment type identifier matching EquipmentTypeResponseItem.name DRY_VAN */
  equipmentType: EquipmentTypeResponseItem['name']
  pickupAddress: Address
  dropOffAddress: Address
  pickupDate: string
  pickupTime: string
  pickupTimeEnd: string
  dropOffDate: string
  dropOffTime: string
  dropOffTimeEnd: string
  phoneNumber: string
  brokerNotes: string
  pickupDateTime: string
  dropOffDateTime: string
  brokerName: string
  pickupFcfs: boolean
  dropOffFcfs: boolean
}

export interface LoadOfferFormPayload extends
  Omit<
    LoadOfferFormDetails,
    'equipmentType' | 'loadPrice' | 'pickupTimeEnd' | 'dropOffTimeEnd'
  >
{
  /** The equipment type value matching EquipmentTypeResponseItem.val 'Dry Van' */
  equipmentType: string
  loadPrice: string | null
  pickupDateEnd: string | null
  pickupTimeEnd: string | null
  dropOffDateEnd: string | null
  dropOffTimeEnd: string | null
  brokerNameOverride: string | null
}

export interface LoadOfferCarrierInfo {
  driverId: number
  carrierName: string
  carrierId: string
  mcNumber: string
}

export interface PreviousOffer {
  load_id: string
  amount: number
  status: string
  pickup: {
    date: string
    date_end: string
    city: string
    state: string
    latitude: number
    longitude: number
    timezone: string
    timezone_name?: string
    fcfs: boolean
  }
  dropoff: {
    date: string
    date_end: string
    city: string
    state: string
    latitude: number
    longitude: number
    timezone: string
    timezone_name?: string
    fcfs: boolean
  }
  equipment_type: string
  total_miles: number
  rate_total_value: number
  rate_per_mile: number
  notes?: string
  contact_phone: string
  broker_name: string
}

export interface FormattedPreviousOffer extends Omit<PreviousOffer, 'equipment_type'> {
  equipment_type: EquipmentTypeResponseItem
}

export interface BookedLoad {
  id: number
  company_name: string
  rate: string
  assumed_total: string
  equipment: string
  origin_date: string
  origin_timezone: string
  origin_address_city: string
  origin_address_state: string
  destination_date: string
  destination_timezone: string
  destination_address_city: string
  destination_address_state: string
}

export interface BookedLoadsResponse {
  booked_loads: BookedLoad[]
}

export interface OfferBid {
  amount: string
  notes: string
}
